/**
 * Generic placeholder shown in the artifact canvas while a fresh artifact is
 * still streaming. Replaces the old "centred spinner on white" holding
 * screen so users see a plausible content shape the moment the canvas opens.
 *
 * Deliberately layout-agnostic — earlier versions of this component
 * dispatched to kind-specific shapes (dashboard, slide deck, grid) but that
 * was actively misleading when the artifact turned out to be an invoice,
 * letter, form, or any non-dashboard page. One neutral skeleton signals
 * "structured content is coming" without committing to a specific layout.
 *
 * Documents render their streamed markdown directly and never mount this
 * component; image artifacts use their own kind-specific placeholder.
 *
 * @module components/artifacts/generation-skeleton
 */

import type { ArtifactKind } from "@taqat/core/types";
import { Skeleton } from "@/components/ui/skeleton";

interface GenerationSkeletonProps {
  /** Artifact kind — used only for the caption ("Drafting your page…" etc.). */
  kind: ArtifactKind;
  /**
   * Optional artifact title — currently unused in the rendered output. Kept
   * on the prop so callers don't need to know the caption depends only on
   * kind; if we ever want to surface the title in the caption (after the
   * init payload exposes it reliably), it slots in here.
   */
  title?: string;
}

/**
 * Full-pane skeleton. Mount this where the old centred spinner lived — the
 * surrounding canvas already handles streaming/finish gating.
 */
export function GenerationSkeleton({ kind }: GenerationSkeletonProps) {
  return (
    <div className="flex h-full w-full flex-col gap-6 overflow-hidden bg-background p-6 sm:p-8">
      <div className="mx-auto flex h-full w-full max-w-4xl flex-1 flex-col gap-5 min-h-0">
        {/* Title block */}
        <div className="flex flex-col gap-2">
          <Skeleton className="h-7 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>

        {/* Primary content area */}
        <Skeleton className="h-40 w-full" />

        {/* Secondary content lines (paragraph / list rhythm) */}
        <div className="flex flex-col gap-3">
          <Skeleton className="h-3 w-11/12" />
          <Skeleton className="h-3 w-9/12" />
          <Skeleton className="h-3 w-10/12" />
          <Skeleton className="h-3 w-8/12" />
        </div>

        {/* Tertiary block */}
        <Skeleton className="h-24 w-full" />

        <div className="flex flex-col gap-3">
          <Skeleton className="h-3 w-10/12" />
          <Skeleton className="h-3 w-7/12" />
        </div>
      </div>

      <p className="shrink-0 text-center text-xs text-muted-foreground">
        Drafting your {kindNoun(kind)}…
      </p>
    </div>
  );
}

function kindNoun(kind: ArtifactKind): string {
  switch (kind) {
    case "html":
      return "page";
    case "presentation":
      return "deck";
    case "spreadsheet":
      return "spreadsheet";
    case "image":
      return "image";
    case "document":
    default:
      return "document";
  }
}
